// src/pages/AcceptInvitePage.jsx
import { useEffect, useState } from 'react'
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react'
import { fetchWithAuth } from '../api/fetchWithAuth'
import { acceptInviteErrorMessage } from './acceptInviteError'

/**
 * Accept-project-invite page. Mounted at /accept-invite/$inviteId via
 * acceptInviteRoute in src/router.jsx.
 *
 * Mirrors AcceptOrgInvitePage structurally — different endpoint, different
 * success copy. Both rely on requireUser middleware having already
 * bounced the user through Keycloak login before this component mounts
 * (thanks to beforeLoad: requireAuth on the route), and both are
 * idempotent on the backend so StrictMode double-invoke is harmless.
 *
 * Note: unlike the org-invite flow, there is no auto-accept middleware
 * for project invites that runs ahead of the page's POST. So the
 * response here is almost always a fresh 204 rather than 'already_accepted'.
 */
export default function AcceptInvitePage({ inviteId, navigate }) {
  const [status, setStatus] = useState('accepting')
  const [message, setMessage] = useState(null)

  useEffect(() => {
    let cancelled = false

    async function accept() {
      try {
        const res = await fetchWithAuth(
          `/api/v1/invites/${inviteId}/accept`,
          { method: 'POST' }
        )

        if (cancelled) return

        if (res.ok) {
          setStatus('success')
          setTimeout(() => {
            if (!cancelled) navigate({ to: '/' })
          }, 1500)
          return
        }

        let body = null
        try { body = await res.json() } catch { /* not JSON */ }
        const errMsg = acceptInviteErrorMessage(res.status, body, 'project')

        setStatus('error')
        setMessage(errMsg)
      } catch (e) {
        if (cancelled) return
        console.error('[accept-invite] failed', e)
        setStatus('error')
        setMessage('Network error. Check your connection and try again.')
      }
    }

    accept()
    return () => { cancelled = true }
  }, [inviteId, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-6">
      <div className="max-w-md w-full bg-bg-soft border border-bg-border rounded-lg p-8 text-center space-y-4">
        {status === 'accepting' && (
          <>
            <Loader2 className="mx-auto animate-spin text-gray-400" size={32} />
            <h1 className="text-lg font-semibold text-gray-200">
              Accepting invitation…
            </h1>
            <p className="text-xs text-gray-400">
              One moment while we add you to the project.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle2 className="mx-auto text-green-400" size={32} />
            <h1 className="text-lg font-semibold text-gray-200">
              You're in.
            </h1>
            <p className="text-xs text-gray-400">
              Redirecting you to the dashboard…
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertTriangle className="mx-auto text-red-400" size={32} />
            <h1 className="text-lg font-semibold text-gray-200">
              Couldn't accept invite
            </h1>
            <p className="text-xs text-gray-400">{message}</p>
            <button
              onClick={() => navigate({ to: '/' })}
              className="mt-2 px-3 py-1.5 rounded bg-bg border border-bg-border
                         text-xs text-gray-300 hover:bg-bg-border transition"
            >
              Go home
            </button>
          </>
        )}
      </div>
    </div>
  )
}